import { useEffect, useState, useMemo, useCallback } from 'react';
import { ProjectStatusBadges } from './ProjectStatusBadges';
import { useAuth } from '../auth/useAuth';
import { isAdminUser } from '../auth/admin';
import type { UserClaims } from '@okta/okta-auth-js';
import Logo from '../assets/logo.png';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import {
  loadAvailableProjects,
  loadProject,
  selectAvailableProjects,
  selectProjectsStatus,
  ProjectItem
} from '../features/thread/threadSlice';
import { ProjectType, type CatalogMode } from '../api/sophia';
import { getCatalogModeLabel } from '../types/catalogMode';
import { abbreviate, distinctValues } from '../utils/projectDisplay';

interface WelcomePageProps {
  onCreateProject: () => void;
}

type ProjectTypeFilter = 'all' | 'optimization' | 'consultation';
type CatalogModeFilter = 'all' | CatalogMode | 'legacy';

const PROJECTS_PAGE_SIZE = 10;

export default function WelcomePage({ onCreateProject }: WelcomePageProps) {
  const { authState, oktaAuth } = useAuth();
  const dispatch = useAppDispatch();
  const projects = useAppSelector(selectAvailableProjects);
  const projectsStatus = useAppSelector(selectProjectsStatus);

  const [user, setUser] = useState<UserClaims | null>(null);
  const [visibleCount, setVisibleCount] = useState(PROJECTS_PAGE_SIZE);

  // Filtri per campo (SPJ-1372)
  const [searchText, setSearchText] = useState('');
  // SPJ-1372: il filtro "Stagione" ora filtra per stagione numerica (es. "272"), non piu' per catalogMode.
  const [seasonFilter, setSeasonFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<ProjectTypeFilter>('all');
  const [consegnaFilter, setConsegnaFilter] = useState('');
  const [macrotemaFilter, setMacrotemaFilter] = useState('');
  const [genderFilter, setGenderFilter] = useState('');
  const [creatorFilter, setCreatorFilter] = useState('');

  const isAdmin = useMemo(() => isAdminUser(user), [user]);

  // Get user info
  useEffect(() => {
    let isMounted = true;

    if (!authState?.isAuthenticated) {
      setUser(null);
      return () => {
        isMounted = false;
      };
    }

    if (oktaAuth && 'getUser' in oktaAuth && typeof oktaAuth.getUser === 'function') {
      oktaAuth
        .getUser()
        .then((info: UserClaims) => {
          if (isMounted) {
            setUser(info);
          }
        })
        .catch((err: Error) => {
          console.error('Failed to get user info:', err);
          if (isMounted) {
            setUser(null);
          }
        });
    }

    return () => {
      isMounted = false;
    };
  }, [authState, oktaAuth]);

  // Load projects on mount
  // SPJ-1372: ricarica i progetti a ogni montaggio della home (la WelcomePage viene smontata
  // quando un progetto è attivo), così tornando in home — es. cliccando il logo — la lista è
  // sempre aggiornata. Durante il refetch si continua a mostrare la lista esistente (no flicker).
  useEffect(() => {
    dispatch(loadAvailableProjects());
  }, [dispatch]);

  const displayName = useMemo(() => {
    if (!user) return 'Utente';
    return user.name ?? user.preferred_username ?? user.email ?? 'Utente';
  }, [user]);

  const formatDate = useCallback((timestamp: number): string => {
    if (!timestamp) return '';

    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return 'Oggi';
    } else if (days === 1) {
      return 'Ieri';
    } else if (days < 7) {
      return `${days} giorni fa`;
    }

    return date.toLocaleDateString(undefined, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }, []);

  const getProjectTypeLabel = useCallback((type?: ProjectType): string => {
    if (!type) return 'Non specificato';
    return type === 'optimization' ? 'Ottimizzazione' : 'Consultazione';
  }, []);

  const getProjectTypeIcon = useCallback((type?: ProjectType): string => {
    if (!type) return '❓';
    return type === 'optimization' ? '⚙️' : '💬';
  }, []);

  const getCatalogModeText = useCallback((mode?: CatalogMode): string => {
    if (!mode) return '—';
    return `${mode} · ${getCatalogModeLabel(mode)}`;
  }, []);

  // Sort projects by last edit time (most recent first)
  const sortedProjects = useMemo(() => {
    return [...projects].sort((a, b) => b.lastEditTime - a.lastEditTime);
  }, [projects]);

  // Distinct values for the select filters, taken from the available projects
  // SPJ-1372: opzioni Stagione = stagioni numeriche distinte, ordinate numericamente ("262" < "272").
  const seasonOptions = useMemo(
    () => distinctValues(projects.map((p) => p.season)).sort((a, b) => Number(a) - Number(b)),
    [projects],
  );
  // SPJ-1372: le consegne (e i macrotemi) dipendono dalla stagione → le opzioni dei filtri
  // contestuali si calcolano solo sui progetti della Stagione selezionata.
  const contextProjects = useMemo(() => {
    return projects.filter((project) => {
      if (seasonFilter !== 'all') return project.season === seasonFilter;
      return true;
    });
  }, [projects, seasonFilter]);
  const consegnaOptions = useMemo(() => distinctValues(contextProjects.map((p) => p.consegna)), [contextProjects]);
  const macrotemaOptions = useMemo(() => distinctValues(contextProjects.map((p) => p.macrotema)), [contextProjects]);
  const genderOptions = useMemo(() => distinctValues(projects.map((p) => p.gender)), [projects]);
  const creatorOptions = useMemo(() => distinctValues(projects.map((p) => p.creatorName)), [projects]);

  // SPJ-1372: cambiare Tipo via da 'optimization' resetta i filtri contestuali,
  // così un filtro nascosto (Linea/Consegna/Macrotema) non filtra silenziosamente.
  const handleTypeFilterChange = useCallback((value: ProjectTypeFilter) => {
    setTypeFilter(value);
    if (value !== 'optimization') {
      setGenderFilter('');
      setConsegnaFilter('');
      setMacrotemaFilter('');
    }
  }, []);

  // SPJ-1372: le consegne/macrotemi validi cambiano con la stagione → al cambio di Stagione
  // azzera i filtri contestuali così non restano valori non più validi per quella stagione.
  const handleSeasonFilterChange = useCallback((value: string) => {
    setSeasonFilter(value);
    setConsegnaFilter('');
    setMacrotemaFilter('');
  }, []);

  // Apply field filters + text search
  const filteredProjects = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    return sortedProjects.filter((project) => {
      if (seasonFilter !== 'all' && project.season !== seasonFilter) return false;
      if (typeFilter !== 'all' && project.projectType !== typeFilter) return false;
      if (consegnaFilter && project.consegna !== consegnaFilter) return false;
      if (macrotemaFilter && project.macrotema !== macrotemaFilter) return false;
      if (genderFilter && project.gender !== genderFilter) return false;
      if (creatorFilter && project.creatorName !== creatorFilter) return false;
      if (query) {
        const haystack = [project.name, project.optimizationId, project.creatorName]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      return true;
    });
  }, [sortedProjects, seasonFilter, typeFilter, consegnaFilter, macrotemaFilter, genderFilter, creatorFilter, searchText]);

  // Reset pagination whenever the filter result changes size/content
  useEffect(() => {
    setVisibleCount(PROJECTS_PAGE_SIZE);
  }, [seasonFilter, typeFilter, consegnaFilter, macrotemaFilter, genderFilter, creatorFilter, searchText]);

  const displayedProjects = useMemo(() => {
    return filteredProjects.slice(0, visibleCount);
  }, [filteredProjects, visibleCount]);

  const hasActiveFilters =
    searchText.trim() !== '' ||
    seasonFilter !== 'all' ||
    typeFilter !== 'all' ||
    consegnaFilter !== '' ||
    macrotemaFilter !== '' ||
    genderFilter !== '' ||
    creatorFilter !== '';

  const resetFilters = useCallback(() => {
    setSearchText('');
    setSeasonFilter('all');
    setTypeFilter('all');
    setConsegnaFilter('');
    setMacrotemaFilter('');
    setGenderFilter('');
    setCreatorFilter('');
  }, []);

  const handleLoadProject = useCallback(async (project: ProjectItem) => {
    if (!project.canLoad) {
      return;
    }

    try {
      await dispatch(loadProject(project.threadId)).unwrap();
    } catch (error) {
      console.error('Failed to load project:', error);
    }
  }, [dispatch]);

  const selectClassName =
    'border border-gray-300 bg-white px-2 py-2 text-sm text-gray-700 focus:border-[#9F6460] focus:outline-none';

  return (
    <div className="flex min-h-screen items-start justify-center bg-white">
      <div className="max-w-5xl w-full px-8 py-12">
        {/* Header */}
        <div className="mb-12">
          <div className="inline-flex items-center gap-x-3 text-3xl bg-[#9F6460] text-white px-6 py-4 font-semibold mb-6">
            <img className="h-8" src={Logo} alt="Logo" />
            <div>PREDICTIVE MERCHANDISING</div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Ciao, {displayName}
          </h1>
        </div>

        {/* Create New Project Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Crea Nuovo Progetto</h2>
          <button
            type="button"
            onClick={onCreateProject}
            className="group relative overflow-hidden bg-white border-2 border-gray-200 hover:border-[#9F6460] transition-all duration-300 p-8 text-left w-full max-w-2xl"
          >
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-16 h-16 bg-[#9F6460] text-white">
                <i className="ti-plus text-3xl" aria-hidden="true" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Nuovo Progetto</h3>
                <p className="text-gray-600">
                  Inizia un nuovo progetto di consultazione o ottimizzazione
                </p>
              </div>
              <div className="flex items-center text-[#9F6460] font-semibold group-hover:translate-x-2 transition-transform duration-300">
                <i className="ti-arrow-right text-2xl" aria-hidden="true" />
              </div>
            </div>
          </button>
        </div>

        {/* Recent Projects Section */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Progetti Recenti</h2>

          {projectsStatus === 'loading' && projects.length === 0 && (
            <div className="flex items-center justify-center p-12">
              <div className="text-center">
                <i className="ti-loader animate-spin text-3xl text-gray-400 mb-4" aria-hidden="true" />
                <p className="text-gray-500">Caricamento progetti...</p>
              </div>
            </div>
          )}

          {projectsStatus === 'succeeded' && projects.length === 0 && (
            <div className="text-center p-12 bg-gray-50 border-2 border-dashed border-gray-300">
              <i className="ti-folder text-5xl text-gray-300 mb-4" aria-hidden="true" />
              <p className="text-gray-500 text-lg">Non hai ancora progetti</p>
              <p className="text-gray-400 text-sm mt-2">Crea il tuo primo progetto per iniziare</p>
            </div>
          )}

          {projects.length > 0 && (
            <>
              {/* Barra filtri per campo (SPJ-1372) */}
              <div className="mb-5 border border-gray-200 bg-gray-50 p-4">
                <div className="flex flex-col gap-3">
                  <div className="flex flex-wrap items-center gap-3">
                    {/* STAGIONE: filtro primario (SPJ-1372) — stagione numerica (es. "272") */}
                    <select
                      value={seasonFilter}
                      onChange={(e) => handleSeasonFilterChange(e.target.value)}
                      className={selectClassName}
                      aria-label="Filtra per stagione"
                    >
                      <option value="all">Stagione: tutte</option>
                      {seasonOptions.map((value) => (
                        <option key={value} value={value}>{value}</option>
                      ))}
                    </select>
                    {/* TIPO */}
                    <div className="inline-flex border border-gray-300 bg-white">
                      {([
                        ['all', 'Tutti'],
                        ['optimization', 'Ottimizzazione'],
                        ['consultation', 'Consultazione'],
                      ] as Array<[ProjectTypeFilter, string]>).map(([value, label]) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => handleTypeFilterChange(value)}
                          className={`px-3 py-2 text-sm font-semibold transition-colors ${
                            typeFilter === value
                              ? 'bg-[#9F6460] text-white'
                              : 'bg-white text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    {/* Ricerca testo (sempre visibile) */}
                    <div className="relative min-w-[220px] flex-1">
                      <i className="ti-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
                      <input
                        type="text"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        placeholder="Cerca per titolo, id o creatore"
                        className="w-full border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-700 focus:border-[#9F6460] focus:outline-none"
                      />
                    </div>
                    {/* Creatore (sempre visibile) */}
                    {creatorOptions.length > 0 && (
                      <select
                        value={creatorFilter}
                        onChange={(e) => setCreatorFilter(e.target.value)}
                        className={selectClassName}
                        aria-label="Filtra per creatore"
                      >
                        <option value="">Creatore: tutti</option>
                        {creatorOptions.map((value) => (
                          <option key={value} value={value}>{value}</option>
                        ))}
                      </select>
                    )}
                    {hasActiveFilters && (
                      <button
                        type="button"
                        onClick={resetFilters}
                        className="px-3 py-2 text-sm font-semibold text-[#9F6460] hover:underline"
                      >
                        Azzera filtri
                      </button>
                    )}
                  </div>
                  {/* Filtri contestuali: solo per Ottimizzazione (Linea, poi Consegna, poi Macrotema) */}
                  {typeFilter === 'optimization' && (
                    <div className="flex flex-wrap items-center gap-3">
                      <select
                        value={genderFilter}
                        onChange={(e) => setGenderFilter(e.target.value)}
                        className={selectClassName}
                        aria-label="Filtra per linea"
                      >
                        <option value="">Linea: tutte</option>
                        {genderOptions.map((value) => (
                          <option key={value} value={value}>{value}</option>
                        ))}
                      </select>
                      <select
                        value={consegnaFilter}
                        onChange={(e) => setConsegnaFilter(e.target.value)}
                        className={selectClassName}
                        aria-label="Filtra per consegna"
                      >
                        <option value="">Consegna: tutte</option>
                        {consegnaOptions.map((value) => (
                          <option key={value} value={value}>{value}</option>
                        ))}
                      </select>
                      <select
                        value={macrotemaFilter}
                        onChange={(e) => setMacrotemaFilter(e.target.value)}
                        className={selectClassName}
                        aria-label="Filtra per macrotema"
                      >
                        <option value="">Macrotema: tutti</option>
                        {macrotemaOptions.map((value) => (
                          <option key={value} value={value}>{value}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>

              {filteredProjects.length === 0 ? (
                <div className="text-center p-10 bg-gray-50 border-2 border-dashed border-gray-300">
                  <p className="text-gray-500">Nessun progetto corrisponde ai filtri selezionati.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {displayedProjects.map((project) => {
                    const consegnaSigla = abbreviate(project.consegna);
                    const macrotemaSigla = abbreviate(project.macrotema);
                    return (
                      <div
                        key={project.threadId}
                        onClick={() => handleLoadProject(project)}
                        className={`group flex items-center gap-4 p-4 border-2 transition-all duration-200 ${
                          project.canLoad
                            ? 'cursor-pointer bg-white border-gray-200 hover:border-[#9F6460]'
                            : 'cursor-not-allowed bg-gray-50 border-gray-200 opacity-70'
                        }`}
                      >
                        <div className="flex items-center justify-center w-12 h-12 bg-gray-100 text-2xl">
                          {getProjectTypeIcon(project.projectType)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            {project.optimizationId && (
                              <span className="border border-gray-300 bg-gray-50 px-2 py-0.5 font-mono text-[11px] font-semibold text-gray-700">
                                #{project.optimizationId}
                              </span>
                            )}
                            <h3 className="font-bold text-gray-900 truncate">{project.name}</h3>
                            {project.season && (
                              <span className="border border-[#9F6460] bg-[#9F6460]/10 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-[#9F6460]">
                                Stagione {project.season}
                              </span>
                            )}
                            <span className="border border-gray-300 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-gray-700">
                              {getCatalogModeText(project.catalogMode)}
                            </span>
                            {isAdmin && project.incognito && (
                              <span className="border border-purple-300 bg-purple-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-purple-700">
                                incognito/test
                              </span>
                            )}
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
                            {project.gender && (
                              <span className="text-sm text-gray-600">
                                <span className="text-gray-400">Linea:</span> {project.gender}
                              </span>
                            )}
                            {consegnaSigla.label && (
                              <span className="text-sm text-gray-600" title={consegnaSigla.title}>
                                <span className="text-gray-400">Consegna:</span> {consegnaSigla.label}
                              </span>
                            )}
                            {macrotemaSigla.label && (
                              <span className="text-sm text-gray-600" title={macrotemaSigla.title}>
                                <span className="text-gray-400">Macrotema:</span> {macrotemaSigla.label}
                              </span>
                            )}
                            <span className={`text-sm font-medium ${
                              project.projectType === 'optimization' ? 'text-blue-700' :
                              project.projectType === 'consultation' ? 'text-green-700' :
                              'text-gray-500'
                            }`}>
                              {getProjectTypeLabel(project.projectType)}
                            </span>
                            {(project.creatorName || project.lastEditorName) && (
                              // SPJ-1372: sotto il creatore, chi ha fatto l'ultima modifica
                              <span className="inline-flex flex-col text-sm leading-tight text-gray-600">
                                {project.creatorName && (
                                  <span><span className="text-gray-400">Creato da:</span> {project.creatorName}</span>
                                )}
                                {project.lastEditorName && (
                                  <span
                                    className="text-teal-600"
                                    title={project.lastEditorTime ? `Ultima modifica: ${new Date(project.lastEditorTime).toLocaleString('it-IT')}` : 'Ultima modifica'}
                                  >
                                    <span className="text-gray-400">Modificato da:</span> {project.lastEditorName}
                                  </span>
                                )}
                              </span>
                            )}
                            <span className="text-sm text-gray-500">
                              {formatDate(project.lastEditTime)}
                            </span>
                          </div>
                          {!project.canLoad && project.loadDisabledReason && (
                            <div className="mt-1 text-xs text-amber-700">
                              {project.lockedByName && <i className="ti-lock mr-1" aria-hidden="true" />}
                              {project.loadDisabledReason}
                            </div>
                          )}
                          <ProjectStatusBadges project={project} />
                        </div>
                        {project.canLoad && (
                          <div className="flex items-center text-[#9F6460] opacity-0 group-hover:opacity-100 group-hover:translate-x-2 transition-all duration-300">
                            <i className="ti-arrow-right text-xl" aria-hidden="true" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Paginazione semplice: mostra altri, mantenendo l'ordinamento per data desc */}
              {displayedProjects.length < filteredProjects.length && (
                <div className="mt-6 text-center">
                  <button
                    type="button"
                    onClick={() => setVisibleCount((count) => count + PROJECTS_PAGE_SIZE)}
                    className="px-6 py-3 text-sm font-semibold bg-white text-black border-2 border-black hover:bg-black hover:text-white transition-colors duration-200"
                  >
                    Mostra altri ({filteredProjects.length - displayedProjects.length})
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
